import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import AppLayout from '@/components/layout/AppLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft, ArrowRight, CheckCircle2, User, Phone, MapPin,
  Shield, Lock, FileSignature, Save, Send, Info,
  Heart, Brain, Activity, Utensils,
} from 'lucide-react';
import { toast } from 'sonner';

/* ══════ MOCK pre-filled data ══════ */
const prefilled = {
  nome: 'Mario', cognome: 'Bianchi', cf: 'BNCMRA47A15H501X',
  dataNascita: '15/01/1947', eta: 79, sesso: 'M',
  indirizzo: 'Via della Repubblica 22, 00185 Roma (RM)',
  telefono: '+39 06 4455667',
  medico: 'Dr. Amato Luca',
};

const steps = [
  { label: 'Anagrafica', short: '1' },
  { label: 'Valutazione Clinica', short: '2' },
  { label: 'Fabbisogno', short: '3' },
  { label: 'Riepilogo e Firma', short: '4' },
];

/* ══════ STEPPER ══════ */
function Stepper({ current }) {
  return (
    <div className="flex items-center justify-between w-full max-w-2xl mx-auto" data-testid="wizard-stepper">
      {steps.map((s, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <div key={i} className="flex items-center flex-1 last:flex-none">
            <div className="flex flex-col items-center gap-1.5">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all ${done ? 'bg-emerald-500 text-white' : active ? 'bg-primary text-white ring-4 ring-primary/20' : 'bg-muted/40 text-muted-foreground'}`} data-testid={`step-circle-${i}`}>
                {done ? <CheckCircle2 className="w-5 h-5" /> : s.short}
              </div>
              <span className={`text-[10px] font-semibold whitespace-nowrap ${active ? 'text-primary' : done ? 'text-emerald-600' : 'text-muted-foreground'}`}>{s.label}</span>
            </div>
            {i < steps.length - 1 && (
              <div className={`flex-1 h-0.5 mx-3 mt-[-18px] rounded ${done ? 'bg-emerald-400' : 'bg-border/40'}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ══════ RADIO CARD ══════ */
function RadioCard({ selected, onClick, label, desc, testId }) {
  return (
    <button
      onClick={onClick}
      className={`p-4 rounded-xl border-2 text-left transition-all w-full ${selected ? 'border-primary bg-primary/5 shadow-sm' : 'border-border/40 hover:border-border bg-card'}`}
      data-testid={testId}
    >
      <div className="flex items-center gap-3">
        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${selected ? 'border-primary' : 'border-muted-foreground/30'}`}>
          {selected && <div className="w-2.5 h-2.5 rounded-full bg-primary" />}
        </div>
        <div>
          <p className={`text-sm font-semibold ${selected ? 'text-primary' : 'text-foreground'}`}>{label}</p>
          {desc && <p className="text-[11px] text-muted-foreground mt-0.5">{desc}</p>}
        </div>
      </div>
    </button>
  );
}

/* ══════ TOGGLE CHECKBOX ══════ */
function ToggleCheck({ checked, onChange, label, testId }) {
  return (
    <button
      onClick={() => onChange(!checked)}
      className={`px-4 py-3 rounded-xl border-2 text-left transition-all ${checked ? 'border-primary bg-primary/5' : 'border-border/40 bg-card hover:border-border'}`}
      data-testid={testId}
    >
      <div className="flex items-center gap-2.5">
        <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${checked ? 'border-primary bg-primary' : 'border-muted-foreground/30'}`}>
          {checked && <CheckCircle2 className="w-3 h-3 text-white" />}
        </div>
        <span className={`text-xs font-medium ${checked ? 'text-primary' : 'text-foreground'}`}>{label}</span>
      </div>
    </button>
  );
}

/* ══════ VISUAL SELECTOR (horizontal) ══════ */
function VisualSelector({ options, selected, onChange, label, icon: Icon, color, testId }) {
  return (
    <div className="space-y-2" data-testid={testId}>
      <div className="flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4" style={{ color }} />}
        <span className="text-xs font-semibold text-foreground">{label}</span>
      </div>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => (
          <button
            key={o}
            onClick={() => onChange(o)}
            className={`px-4 py-2.5 rounded-xl text-xs font-semibold border-2 transition-all ${selected === o ? 'border-primary bg-primary/8 text-primary' : 'border-border/30 text-muted-foreground hover:border-border'}`}
            data-testid={`${testId}-${o.toLowerCase().replace(/\s/g, '-')}`}
          >
            {o}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ══════ STEP 1: ANAGRAFICA ══════ */
function Step1({ data, setData, showPrefilled = true }) {
  return (
    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6" data-testid="step-1-content">
      {/* Sync notice */}
      {showPrefilled && (
        <div className="flex items-center gap-2.5 px-4 py-3 rounded-xl bg-blue-50/60 border border-blue-100">
          <Info className="w-4 h-4 text-blue-500 flex-shrink-0" />
          <p className="text-[11px] text-blue-700 font-medium">Dati anagrafici sincronizzati con Anagrafe Regionale</p>
        </div>
      )}

      {/* Patient data */}
      <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl">
        <CardContent className="p-6">
          <h3 className="text-sm font-bold text-foreground mb-4 flex items-center gap-2"><User className="w-4 h-4 text-blue-500" />Dati Paziente</h3>
          {showPrefilled ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { label: 'Cognome', value: prefilled.cognome },
                { label: 'Nome', value: prefilled.nome },
                { label: 'Codice Fiscale', value: prefilled.cf },
                { label: 'Data di Nascita', value: `${prefilled.dataNascita} (${prefilled.eta} anni)` },
                { label: 'Indirizzo', value: prefilled.indirizzo, full: true },
              ].map((f) => (
                <div key={f.label} className={f.full ? 'sm:col-span-2' : ''}>
                  <label className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wider">{f.label}</label>
                  <div className="mt-1 px-3 py-2.5 rounded-xl bg-muted/20 border border-border/30 text-xs font-medium text-foreground">{f.value}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { label: 'Cognome', key: 'pazCognome', placeholder: 'Inserisci cognome' },
                { label: 'Nome', key: 'pazNome', placeholder: 'Inserisci nome' },
                { label: 'Codice Fiscale', key: 'pazCf', placeholder: 'RSSMRA47A15H501X' },
                { label: 'Data di Nascita', key: 'pazDataNascita', placeholder: 'GG/MM/AAAA' },
                { label: 'Indirizzo di Residenza', key: 'pazIndirizzo', placeholder: 'Via, Numero, CAP, Citta', full: true },
                { label: 'Telefono', key: 'pazTelefono', placeholder: '+39 ...' },
                { label: 'Medico di Base', key: 'pazMedico', placeholder: 'Dr. ...' },
              ].map((f) => (
                <div key={f.key} className={f.full ? 'sm:col-span-2' : ''}>
                  <label className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wider">{f.label}</label>
                  <input
                    type="text"
                    value={data[f.key] || ''}
                    onChange={e => setData(prev => ({ ...prev, [f.key]: e.target.value }))}
                    placeholder={f.placeholder}
                    className="mt-1 w-full px-3 py-2.5 rounded-xl bg-card border border-border/50 text-xs font-medium text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50"
                    data-testid={`input-${f.key}`}
                  />
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Caregiver */}
      <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl">
        <CardContent className="p-6">
          <h3 className="text-sm font-bold text-foreground mb-4 flex items-center gap-2"><Phone className="w-4 h-4 text-emerald-500" />Caregiver / Persona di Riferimento</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wider">Nome e Cognome</label>
              <input className="mt-1 w-full px-3 py-2.5 rounded-xl border border-border bg-card text-xs font-medium focus:outline-none focus:ring-2 focus:ring-primary/30" placeholder="Es. Bianchi Laura" value={data.caregiverNome} onChange={(e) => setData({...data, caregiverNome: e.target.value})} data-testid="input-caregiver-nome" />
            </div>
            <div>
              <label className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wider">Telefono</label>
              <input className="mt-1 w-full px-3 py-2.5 rounded-xl border border-border bg-card text-xs font-medium focus:outline-none focus:ring-2 focus:ring-primary/30" placeholder="+39 ..." value={data.caregiverTel} onChange={(e) => setData({...data, caregiverTel: e.target.value})} data-testid="input-caregiver-tel" />
            </div>
            <div>
              <label className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wider">Relazione</label>
              <input className="mt-1 w-full px-3 py-2.5 rounded-xl border border-border bg-card text-xs font-medium focus:outline-none focus:ring-2 focus:ring-primary/30" placeholder="Es. Figlia" value={data.caregiverRel} onChange={(e) => setData({...data, caregiverRel: e.target.value})} data-testid="input-caregiver-rel" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Supporto Sociale */}
      <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl">
        <CardContent className="p-6">
          <h3 className="text-sm font-bold text-foreground mb-4 flex items-center gap-2"><MapPin className="w-4 h-4 text-violet-500" />Supporto Sociale</h3>
          <p className="text-[11px] text-muted-foreground mb-3 font-medium">Il paziente vive:</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            {['Solo', 'Con coniuge', 'Con figli', 'Altri'].map((o) => (
              <RadioCard key={o} selected={data.vive === o} onClick={() => setData({...data, vive: o})} label={o} testId={`radio-vive-${o.toLowerCase().replace(/\s/g, '-')}`} />
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground mb-3 font-medium">Assistito da:</p>
          <div className="grid grid-cols-3 gap-3">
            {['Familiari', 'Badante', 'Altri'].map((o) => (
              <ToggleCheck key={o} checked={data.assistitoDa.includes(o)} onChange={(v) => setData({...data, assistitoDa: v ? [...data.assistitoDa, o] : data.assistitoDa.filter(x => x !== o)})} label={o} testId={`check-assistito-${o.toLowerCase()}`} />
            ))}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

/* ══════ STEP 2: VALUTAZIONE CLINICA ══════ */
function Step2({ data, setData }) {
  return (
    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6" data-testid="step-2-content">
      {/* Diagnosi */}
      <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl">
        <CardContent className="p-6">
          <h3 className="text-sm font-bold text-foreground mb-4 flex items-center gap-2"><Heart className="w-4 h-4 text-red-500" />Diagnosi Principale</h3>
          <label className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wider">Diagnosi e descrizione problema principale (Codice ICD-9 suggerito)</label>
          <textarea className="mt-1.5 w-full px-4 py-3 rounded-xl border border-border bg-card text-sm resize-none h-20 focus:outline-none focus:ring-2 focus:ring-primary/30" placeholder="Es. Scompenso cardiaco acuto in BPCO riacutizzata (ICD-9: 428.0, 491.21)" value={data.diagnosi} onChange={(e) => setData({...data, diagnosi: e.target.value})} data-testid="input-diagnosi" />
        </CardContent>
      </Card>

      {/* Condizioni Cliniche */}
      <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl">
        <CardContent className="p-6">
          <h3 className="text-sm font-bold text-foreground mb-4">Condizioni Cliniche Attuali</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
            {['Diabete', 'Cardiopatia', 'Insuff. respiratoria', 'Demenza', 'Neoplasie', 'Ictus/TIA', 'Insuff. renale', 'BPCO', 'Altro'].map((c) => (
              <ToggleCheck key={c} checked={data.condizioni.includes(c)} onChange={(v) => setData({...data, condizioni: v ? [...data.condizioni, c] : data.condizioni.filter(x => x !== c)})} label={c} testId={`check-cond-${c.toLowerCase().replace(/[\s/.]/g, '-')}`} />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Scale di Valutazione */}
      <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl">
        <CardContent className="p-6 space-y-6">
          <h3 className="text-sm font-bold text-foreground">Scale di Valutazione</h3>
          <VisualSelector label="Stabilita Clinica" icon={Activity} color="#3B82F6" options={['Stabile', 'Moderatamente instabile', 'Instabile']} selected={data.stabilita} onChange={(v) => setData({...data, stabilita: v})} testId="sel-stabilita" />
          <VisualSelector label="Stato di Coscienza" icon={Brain} color="#8B5CF6" options={['Lucido', 'Confuso', 'Soporoso', 'Coma']} selected={data.coscienza} onChange={(v) => setData({...data, coscienza: v})} testId="sel-coscienza" />

          {/* Pain Slider */}
          <div data-testid="sel-dolore">
            <div className="flex items-center gap-2 mb-2">
              <Heart className="w-4 h-4 text-red-400" />
              <span className="text-xs font-semibold text-foreground">Dolore (NRS 0-10)</span>
              <Badge className="ml-auto text-sm font-bold border-0" style={{ backgroundColor: data.dolore > 6 ? '#EF444418' : data.dolore > 3 ? '#F59E0B18' : '#10B98118', color: data.dolore > 6 ? '#EF4444' : data.dolore > 3 ? '#F59E0B' : '#10B981' }}>{data.dolore}</Badge>
            </div>
            <input type="range" min="0" max="10" value={data.dolore} onChange={(e) => setData({...data, dolore: parseInt(e.target.value)})} className="w-full h-2 rounded-full appearance-none bg-gradient-to-r from-emerald-300 via-amber-300 to-red-400 cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-primary" data-testid="slider-dolore" />
            <div className="flex justify-between mt-1">
              <span className="text-[9px] text-muted-foreground">Nessun dolore</span>
              <span className="text-[9px] text-muted-foreground">Dolore massimo</span>
            </div>
          </div>

          <VisualSelector label="Mobilita" icon={Activity} color="#06B6D4" options={['Indipendente', 'Con aiuto', 'Totalmente dipendente']} selected={data.mobilita} onChange={(v) => setData({...data, mobilita: v})} testId="sel-mobilita" />
          <VisualSelector label="Alimentazione" icon={Utensils} color="#F59E0B" options={['Autonoma', 'Assistita', 'Rifiuta', 'Artificiale']} selected={data.alimentazione} onChange={(v) => setData({...data, alimentazione: v})} testId="sel-alimentazione" />
        </CardContent>
      </Card>
    </motion.div>
  );
}

/* ══════ STEP 3: FABBISOGNO ══════ */
function Step3({ data, setData }) {
  return (
    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6" data-testid="step-3-content">
      <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl">
        <CardContent className="p-6">
          <h3 className="text-sm font-bold text-foreground mb-4">Necessita Particolari</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
            {['Ossigenoterapia', 'Ventilazione assistita', 'Tracheostomia', 'Catetere vescicale', 'Nutrizione enterale', 'Nutrizione parenterale', 'Ulcere cutanee', 'Aspirazione tracheale', 'Terapia infusionale'].map((n) => (
              <ToggleCheck key={n} checked={data.necessita.includes(n)} onChange={(v) => setData({...data, necessita: v ? [...data.necessita, n] : data.necessita.filter(x => x !== n)})} label={n} testId={`check-nec-${n.toLowerCase().replace(/\s/g, '-')}`} />
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl">
        <CardContent className="p-6">
          <h3 className="text-sm font-bold text-foreground mb-4">Percorso Assistenziale Richiesto</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { key: 'ADI', label: 'ADI', desc: 'Assistenza Domiciliare Integrata' },
              { key: 'RSA', label: 'RSA', desc: 'Residenza Sanitaria Assistenziale' },
              { key: 'Riab. Territoriale', label: 'Riab. Territoriale', desc: 'Riabilitazione sul territorio' },
            ].map((p) => (
              <RadioCard key={p.key} selected={data.percorso === p.key} onClick={() => setData({...data, percorso: p.key})} label={p.label} desc={p.desc} testId={`radio-percorso-${p.key.toLowerCase().replace(/[\s.]/g, '-')}`} />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Note aggiuntive */}
      <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl">
        <CardContent className="p-6">
          <h3 className="text-sm font-bold text-foreground mb-3">Note Aggiuntive</h3>
          <textarea className="w-full px-4 py-3 rounded-xl border border-border bg-card text-sm resize-none h-20 focus:outline-none focus:ring-2 focus:ring-primary/30" placeholder="Eventuali informazioni aggiuntive per la COT..." value={data.note} onChange={(e) => setData({...data, note: e.target.value})} data-testid="input-note" />
        </CardContent>
      </Card>
    </motion.div>
  );
}

/* ══════ STEP 4: RIEPILOGO + FIRMA ══════ */
function Step4({ data }) {
  const rows = [
    { label: 'Paziente', value: `${prefilled.cognome} ${prefilled.nome} (${prefilled.cf})` },
    { label: 'Caregiver', value: data.caregiverNome ? `${data.caregiverNome} — ${data.caregiverRel} — ${data.caregiverTel}` : 'Non specificato' },
    { label: 'Vive', value: data.vive || 'Non specificato' },
    { label: 'Assistito da', value: data.assistitoDa.length ? data.assistitoDa.join(', ') : 'Non specificato' },
    { label: 'Diagnosi', value: data.diagnosi || 'Non compilata' },
    { label: 'Condizioni', value: data.condizioni.length ? data.condizioni.join(', ') : 'Nessuna selezionata' },
    { label: 'Stabilita', value: data.stabilita || '-' },
    { label: 'Coscienza', value: data.coscienza || '-' },
    { label: 'Dolore NRS', value: `${data.dolore}/10` },
    { label: 'Mobilita', value: data.mobilita || '-' },
    { label: 'Alimentazione', value: data.alimentazione || '-' },
    { label: 'Necessita', value: data.necessita.length ? data.necessita.join(', ') : 'Nessuna' },
    { label: 'Percorso', value: data.percorso || 'Non selezionato' },
    { label: 'Note', value: data.note || '-' },
  ];

  return (
    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6" data-testid="step-4-content">
      <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl">
        <CardContent className="p-6">
          <h3 className="text-sm font-bold text-foreground mb-4 flex items-center gap-2"><Shield className="w-4 h-4 text-indigo-500" />Riepilogo Richiesta ADI</h3>
          <div className="divide-y divide-border/20" data-testid="summary-table">
            {rows.map((r) => (
              <div key={r.label} className="flex items-start py-2.5 gap-4">
                <span className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wider w-28 flex-shrink-0 pt-0.5">{r.label}</span>
                <span className="text-xs text-foreground font-medium">{r.value}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

/* ══════ FIRMA DIGITALE SECTION ══════ */
function FirmaDigitale({ pin, setPin, checked, setChecked, signed }) {
  if (signed) {
    return (
      <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl ring-2 ring-emerald-200" data-testid="firma-signed">
        <CardContent className="p-6 flex items-center justify-center gap-3">
          <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center">
            <CheckCircle2 className="w-6 h-6 text-emerald-500" />
          </div>
          <div>
            <p className="text-sm font-semibold text-emerald-700">Richiesta Firmata e Inviata</p>
            <p className="text-[11px] text-muted-foreground">Inviata alla COT-D per valutazione e triage.</p>
          </div>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl border-l-4 border-l-indigo-400" data-testid="firma-section">
      <CardContent className="p-6 space-y-4">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-xl bg-indigo-50 flex items-center justify-center"><Lock className="w-4 h-4 text-indigo-500" /></div>
          <div>
            <h3 className="text-sm font-bold text-foreground">Firma Digitale</h3>
            <p className="text-[10px] text-muted-foreground">Inserisci il PIN della Smartcard per firmare</p>
          </div>
        </div>
        <div>
          <label className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wider">PIN Smartcard</label>
          <input type="password" className="mt-1 w-48 px-3 py-2.5 rounded-xl border border-border bg-card text-sm font-medium tracking-widest focus:outline-none focus:ring-2 focus:ring-indigo-300/50" placeholder="****" value={pin} onChange={(e) => setPin(e.target.value)} maxLength={8} data-testid="input-pin" />
        </div>
        <button onClick={() => setChecked(!checked)} className="flex items-start gap-2.5 text-left" data-testid="check-dichiarazione">
          <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${checked ? 'border-primary bg-primary' : 'border-muted-foreground/30'}`}>
            {checked && <CheckCircle2 className="w-3 h-3 text-white" />}
          </div>
          <span className="text-[11px] text-muted-foreground leading-relaxed">Dichiaro sotto la mia responsabilita che le informazioni inserite sono veritiere e complete ai fini della richiesta di attivazione ADI.</span>
        </button>
      </CardContent>
    </Card>
  );
}

/* ══════ MAIN WIZARD ══════ */
export default function NewADICreationWizard({ role = 'mmg', backPath = '/mmg', prefillData = true, initialData, initialStep }) {
  const navigate = useNavigate();
  const [current, setCurrent] = useState(initialStep ?? 0);
  const [signed, setSigned] = useState(false);
  const [pin, setPin] = useState('');
  const [dichiarazione, setDichiarazione] = useState(false);
  
  // Default state with all required fields
  const defaultData = {
    caregiverNome: '', caregiverTel: '', caregiverRel: '',
    vive: '', assistitoDa: [],
    diagnosi: '', condizioni: [], stabilita: '', coscienza: '',
    dolore: 0, mobilita: '', alimentazione: '',
    necessita: [], percorso: '', note: '',
  };
  
  // Merge initialData with defaults to ensure all arrays exist
  const [data, setData] = useState(initialData ? { ...defaultData, ...initialData } : defaultData);

  const next = () => setCurrent((p) => Math.min(p + 1, 3));
  const prev = () => setCurrent((p) => Math.max(p - 1, 0));

  const handleSign = () => {
    setSigned(true);
    toast.success('Richiesta firmata e inviata', { description: 'La COT-D ricevera la richiesta per il triage entro 24h.' });
  };

  const handleDraft = () => {
    toast.success('Bozza salvata', { description: 'Puoi riprendere la compilazione in qualsiasi momento.' });
    navigate(backPath);
  };

  const canSign = current === 3 && pin.length >= 4 && dichiarazione;

  return (
    <AppLayout role={role}>
      <div className="space-y-6 max-w-4xl mx-auto" data-testid="adi-wizard">
        {/* Header */}
        <div className="flex items-center justify-between">
          <button onClick={() => navigate(backPath)} className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors group" data-testid="back-to-dashboard">
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />Torna alla Dashboard
          </button>
          <Badge className="text-[11px] font-bold px-3 py-1.5 border-0 bg-blue-50 text-blue-600">Scheda Unica Regionale — Mod. 431</Badge>
        </div>

        {/* Stepper */}
        <div className="py-2">
          <Stepper current={current} />
        </div>

        {/* Step Content */}
        <AnimatePresence mode="wait">
          {current === 0 && <Step1 key="s1" data={data} setData={setData} showPrefilled={prefillData} />}
          {current === 1 && <Step2 key="s2" data={data} setData={setData} />}
          {current === 2 && <Step3 key="s3" data={data} setData={setData} />}
          {current === 3 && <Step4 key="s4" data={data} />}
        </AnimatePresence>

        {/* Firma (only on step 4) */}
        {current === 3 && <FirmaDigitale pin={pin} setPin={setPin} checked={dichiarazione} setChecked={setDichiarazione} signed={signed} />}

        {/* Navigation buttons */}
        {!signed && (
          <div className="flex items-center justify-between pt-2 pb-4" data-testid="wizard-nav-buttons">
            <div>
              {current > 0 && (
                <Button variant="outline" className="gap-2 rounded-xl" onClick={prev} data-testid="btn-prev">
                  <ArrowLeft className="w-4 h-4" />Indietro
                </Button>
              )}
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" className="gap-2 rounded-xl" onClick={handleDraft} data-testid="btn-bozza">
                <Save className="w-4 h-4" />Salva in Bozza
              </Button>
              {current < 3 ? (
                <Button className="gap-2 rounded-xl bg-primary hover:bg-primary/90 text-white px-6" onClick={next} data-testid="btn-next">
                  Avanti<ArrowRight className="w-4 h-4" />
                </Button>
              ) : (
                <Button className="gap-2 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white px-6" disabled={!canSign} onClick={handleSign} data-testid="btn-firma-invia">
                  <FileSignature className="w-4 h-4" />Apponi Firma e Invia a COT-D
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
